import Dompurify from 'dompurify';

import Tools from '../Tools/Tools';
/**
 * Třída pro vytváření koláčového grafu.
 */
class PieGraph {
	constructor(ctx, header, data, dimension) {
		this.ctx = ctx;
		this.header = header;
		this.data = data;
		this.setGraphVisibility(dimension, header.field);

		// Config grafu.
		this.options = {
			elements: {
				arc: {
					borderWidth: 0, // zruší border mezi koláči
				},
			},
			legend: {
				display: false,
				position: 'bottom',
			},
			legendCallback: this.createCustomLegend.bind(this),
			segmentShowStroke: false,
			tooltips: {
				callbacks: {
					// Pokud nejsou pro nějaký pieChart data, tak se používají fejkový (1, 1, 1..) pro
					// vygenerování grafu v takovém případě, ale tooltip musí ukazovat hodnotu 0.
					label: (tooltipItem, data_) => {
						if (this.header.value) {
							return (
								data_.labels[tooltipItem.index] +
								': ' +
								Tools.formatValue(data_.datasets[0].data[tooltipItem.index], this.header.type)
							);
						} else {
							return data_.labels[tooltipItem.index] + ': 0';
						}
					},
				},
			},
		};

		// Barvy pro mobil, desktop, tablet, ostatní.
		this.colors = ['#CC0000', '#3A3A3A', '#F9CDCE', '#E6E6E6'];
		// Barva světle šedého grafu, kde jsou všechny hodnoty 0.
		this.colorNoData = '#F3F3F3';
		this.createGraph();
	}

	/**
	 * Graph is hidden for views chart with dsp dimension filter.
	 * @param {string} dimension
	 * @param {string} field
	 */
	setGraphVisibility(dimension, field) {
		const graphWrapper = Tools.getParentByClass(this.ctx.canvas, 'GraphPie-wrapper');

		this.isHidden = dimension === 'byDsp' && field === 'views';

		graphWrapper.classList.toggle('is-hidden', this.isHidden);
	}

	/**
	 * Labels - popisky na ose Y.
	 * ! jejich počet musí odpovídat počtům hodnot, jinak knihovna selže.
	 * @return {[]} Pole labelů.
	 */
	getLabels() {
		return this.data.map((value) => value.name);
	}

	/**
	 * Updatuje data pro graf a překreslí ho.
	 * @param {[]} data
	 */
	updateDataset(data, header, dimension) {
		this.data = data;
		this.header = header;
		this.setGraphVisibility(dimension, header.field);

		if (this.isHidden) {
			return;
		}

		this.graph.data.labels = this.getLabels();
		this.graph.data.datasets = [this.getDataset()];
		this.createLegend();
		this.graph.update();
	}

	/**
	 * Naformátuje hodnoty v grafu.
	 * @return {[]} Vrací pole objektů
	 */
	getDataset() {
		let data = [];
		let colors = [];

		// Pokud header.value === 0, tak Chart.js negeneruje graf pro 0 hodnoty,
		// takže fejkuju data ([1, 1, 1]), aby se zobrazil graf, který je celý zašedlý.
		if (this.header.value) {
			data = this.data.map((record) => record.value);
			colors = this.colors;
		} else {
			data = this.data.map(() => 1);
			colors = this.colors.map(() => this.colorNoData);
		}
		return { backgroundColor: colors, data };
	}

	/**
	 * Naformátuje data pro graf.
	 * @return {object}
	 */
	formatData() {
		return {
			datasets: [this.getDataset()],
			labels: this.getLabels(),
		};
	}

	/**
	 * Vytvoří legendu grafu.
	 * Pokud už existuje, tak jí pouze obnoví.
	 */
	createLegend() {
		let [legend] = this.ctx.canvas.parentNode.getElementsByClassName('Graph-legend');

		if (!legend) {
			legend = Tools.createElement('div', {
				class: 'Graph-legend',
				// nosemgrep
				innerHTML: Dompurify.sanitize(this.graph.generateLegend()),
			});

			this.ctx.canvas.parentNode.appendChild(legend);
		} else {
			// nosemgrep
			legend.innerHTML = Dompurify.sanitize(this.graph.generateLegend());
		}
	}

	/**
	 * Pod každým koláčovým grafem je legenda, kde u jmen (mobil, desktop apod.)
	 * jsou procentuální hodnoty. Zde je získán součet pro jejich následný výpočet.
	 * @param {[]} graphData
	 */
	getSumOfValues(graphData) {
		// Sečte všechny hodnoty pro následný výpočet procent.
		let valuesSum = graphData.data.reduce((prev, current) => prev + current, 0);

		// Pokud je header.value 0, tak valuesSum musí být také 0
		if (valuesSum && this.header.value) {
			valuesSum = 100 / valuesSum;
		} else {
			valuesSum = 0;
		}

		return valuesSum;
	}

	/**
	 * Legenda Chart.js není dostačující, takže
	 * si vytváříme vlastní.
	 * @param {object} chart Data grafu dodané knihovnou.
	 * @return {string} Vrací string s HTML.
	 */
	createCustomLegend(chart) {
		const graphLabels = chart.data.labels;
		const [graphData] = chart.data.datasets; // pieChart má pouze jeden dataset
		const valuesSum = this.getSumOfValues(graphData);

		let legend = `
		<h4 class='Graph-legend-title'>
			${this.header.title}
			<span>
				${Tools.formatValue(this.header.value, this.header.type)}
			</span>
        </h4>`;

		if (!valuesSum) {
			return legend;
		}

		legend += '<ul class="Graph-legend-list">';

		graphLabels.forEach((label, index) => {
			if (graphData.data[index]) {
				legend += `
                <li class='Graph-legend-item'>
                    <span class='Graph-legend-bullet' style="background-color:${
											graphData.backgroundColor[index]
										}"></span>
                    <span class='Graph-legend-value'>${Tools.formatPercent(valuesSum * graphData.data[index])}</span>
                    <span class='Graph-legend-name'>${label}</span>
                </li>`;
			}
		});

		legend += '</ul>';

		return legend;
	}

	/**
	 * Vytvoří nový graf.
	 */
	createGraph() {
		this.graph = new Chart(this.ctx, {
			data: this.formatData(),
			options: this.options,
			type: 'doughnut',
		});

		this.createLegend();
	}
}

export default PieGraph;
